import { useAuth0 } from "@auth0/auth0-react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDidUpdate } from "@mantine/hooks";
import { Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { ReactNode, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import { isDayjsBeforeToday } from "../../utils/dayjs/isDayjsBeforeToday";
import { isValidDateOfBirth } from "../../utils/dayjs/isValidDateOfBirth";
import { preventFormSubmitOnEnter } from "../../utils/formUtils";
import { onlyContainsNumbers, yupValidatePostalCode, yupValidateState } from "../../utils/validationUtils";
import { fullNameRequired, noNumbersAllowed } from "../../utils/validators";
import { PhoneNumberAlert } from "../alerts/PhoneNumberAlert";
import { ControllerCountrySelect } from "../form/ControllerCountrySelect";
import { ControllerTextField } from "../form/ControllerTextField";
import { FormCheckbox } from "../form/FormCheckbox";
import { FormDatePicker } from "../form/FormDatePicker";
import { FormInputWrapper } from "../form/FormInputWrapper";
import { FormPhoneInput, isPhoneNumberPossible } from "../form/FormPhoneInput";
import { FormSpreadAddressInput, IAddressFieldPaths } from "../form/FormSpreadAddressInput";
import { FormStringOptionSelect } from "../form/FormStringOptionSelect";
import { FormTextInput } from "../form/FormTextInput";
import { InfoTooltip } from "../InfoTooltip";
import { SSN_REGEX, TITLE_OPTIONS } from "./constants";
import { IOfficerInformationFormData } from "./FormData";
import { usePassportCountryOptions } from "./usePassportCountryOptions";

const phoneNumberFullTest: yup.TestConfig<string | undefined> = {
    message: "Phone Number is invalid",
    test: (value) => isPhoneNumberPossible(value ?? ""),
};

const stringRequiredWhenIsNotOfficer = (test?: yup.TestConfig<string | undefined>) =>
    yup.string().when("isOfficer", {
        is: false,
        then: (schema) => (test ? schema.required().test(test) : schema.required()),
    });

export const formSchema = yup.object({
    firstName: stringRequiredWhenIsNotOfficer(fullNameRequired).test(noNumbersAllowed).label("First Name"),
    lastName: stringRequiredWhenIsNotOfficer(fullNameRequired).test(noNumbersAllowed).label("Last Name"),
    title: yup.string().required().label("Title"),
    phoneNumberFull: stringRequiredWhenIsNotOfficer(phoneNumberFullTest).label("Phone Number"),
    phoneCountryCode: stringRequiredWhenIsNotOfficer(),
    phoneNumber: stringRequiredWhenIsNotOfficer(),
    email: stringRequiredWhenIsNotOfficer().email("Email address is invalid").label("Email"),
    ssn: yup
        .string()
        .when("usePassport", {
            is: false,
            then: (schema) => schema.required().matches(SSN_REGEX, "SSN is invalid"),
        })
        .label("SSN"),
    passportNumber: yup
        .string()
        .when("usePassport", {
            is: true,
            then: (schema) => schema.required(),
        })
        .label("Passport Number"),
    nationality: yup
        .string()
        .when("usePassport", {
            is: true,
            then: (schema) => schema.required(),
        })
        .label("Nationality"),
    dob: yup
        .date()
        .required()
        .label("Date of Birth")
        .nullable()
        .test({
            message: "Date of Birth must be in the past.",
            test: (value) => isDayjsBeforeToday(dayjs(value)),
        })
        .test({
            message: "Date of Birth must be valid.",
            test: (value) => !isValidDateOfBirth(dayjs(value)),
        }),
    addressStreet: yup
        .string()
        .required()
        .test({
            message: "Street address must be valid",
            test: (value, _ctx) => !onlyContainsNumbers(value ?? ""),
        })
        .label("Street"),
    addressStreet2: yup.string().optional(),
    addressCity: yup
        .string()
        .required()
        .test({
            message: "City must be valid",
            test: (value, _ctx) => !onlyContainsNumbers(value ?? ""),
        })
        .label("City"),
    addressDistrict: yupValidateState("addressCountry").label("State / Province"),
    addressZipcode: yupValidatePostalCode("addressCountry").label("ZIP Code"),
    addressCountry: yup.string().nullable().required().label("Country"),
    bcFirstName: yup.string().required().test(fullNameRequired).test(noNumbersAllowed).label("First Name"),
    bcLastName: yup.string().required().test(fullNameRequired).test(noNumbersAllowed).label("Last Name"),
    bcEmail: yup.string().required().label("Email"),
    bcPhoneNumberFull: yup.string().required().test(phoneNumberFullTest).label("Mobile Phone Number"),
    bcPhoneCountryCode: yup.string().required(),
    bcPhoneNumber: yup.string().required(),
});

const ADDRESS_FIELDS: IAddressFieldPaths<IOfficerInformationFormData> = {
    street: "addressStreet",
    street2: "addressStreet2",
    city: "addressCity",
    state: "addressDistrict",
    zipCode: "addressZipcode",
    country: "addressCountry",
};

interface IProps {
    defaultValues?: IOfficerInformationFormData;
    onSubmit: SubmitHandler<IOfficerInformationFormData>;
    prevPageButton?: ReactNode;
    nextPageButton?: ReactNode;
}

export const OfficerInformationForm: React.FC<IProps> = ({
    defaultValues,
    onSubmit,
    prevPageButton,
    nextPageButton,
}) => {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);

    const resolver = yupResolver(formSchema);
    const methods = useForm<IOfficerInformationFormData>({
        mode: "onBlur",
        resolver,
        defaultValues: { isOfficer: true, ...defaultValues },
    });
    const { handleSubmit, resetField, setValue, watch } = methods;

    const { user } = useAuth0();
    useEffect(() => {
        if (user?.email) {
            setValue("bcEmail", user.email);
        }
    }, [setValue, user?.email]);

    const usePassport = watch("usePassport");
    useEffect(() => {
        if (usePassport) {
            resetField("ssn", { defaultValue: "" });
        } else {
            resetField("passportNumber", { defaultValue: "" });
            resetField("nationality", { defaultValue: "" });
        }
    }, [resetField, usePassport]);

    const [bcFirstName, bcLastName, bcEmail, bcPhoneNumberFull, bcPhoneCountryCode, bcPhoneNumber, isOfficer] = watch([
        "bcFirstName",
        "bcLastName",
        "bcEmail",
        "bcPhoneNumberFull",
        "bcPhoneCountryCode",
        "bcPhoneNumber",
        "isOfficer",
    ]);

    useDidUpdate(() => {
        if (isOfficer) {
            setValue("firstName", bcFirstName ?? "");
            setValue("lastName", bcLastName ?? "");
            setValue("email", bcEmail ?? "");
            setValue("phoneNumberFull", bcPhoneNumberFull ?? "");
            setValue("phoneCountryCode", bcPhoneCountryCode ?? "");
            setValue("phoneNumber", bcPhoneNumber ?? "");
        }
    }, [bcFirstName, bcLastName, bcEmail, bcPhoneNumberFull, bcPhoneCountryCode, bcPhoneNumber, isOfficer, setValue]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventFormSubmitOnEnter}>
                <Stack spacing={3}>
                    <Typography fontWeight="bold">My Information</Typography>
                    <Stack direction="row" spacing={2}>
                        <FormTextInput label="First Name" name="bcFirstName" required={true} />
                        <FormTextInput label="Last Name" name="bcLastName" required={true} />
                    </Stack>
                    <FormTextInput label="Email" name="bcEmail" disabled={true} required={true} />
                    <FormPhoneInput
                        label="Mobile Phone Number"
                        fullPhoneFieldName="bcPhoneNumberFull"
                        countryCodeFieldName="bcPhoneCountryCode"
                        localNumberFieldName="bcPhoneNumber"
                        required={true}
                    />
                    <PhoneNumberAlert />
                    <Stack alignItems="center" direction="row" spacing={1}>
                        <FormCheckbox
                            name="isOfficer"
                            label="I am an officer at the business"
                            onChange={(event) => {
                                const nextIsOfficer = event.target.value;

                                resetField("firstName", {
                                    defaultValue: (nextIsOfficer && bcFirstName) || "",
                                });
                                resetField("lastName", {
                                    defaultValue: (nextIsOfficer && bcLastName) || "",
                                });
                                resetField("email", {
                                    defaultValue: (nextIsOfficer && bcEmail) || "",
                                });
                                resetField("phoneNumberFull", {
                                    defaultValue: (nextIsOfficer && bcPhoneNumberFull) || "",
                                });
                                resetField("phoneCountryCode", {
                                    defaultValue: (nextIsOfficer && bcPhoneCountryCode) || "",
                                });
                                resetField("phoneNumber", {
                                    defaultValue: (nextIsOfficer && bcPhoneNumber) || "",
                                });
                            }}
                        />
                        <InfoTooltip tooltip="Officers have the authority to act and sign on behalf of the business. Officers are generally, but not limited to the CEO, CFO, and vice presidents." />
                    </Stack>
                    <Typography fontWeight="bold" pt={1}>
                        Officer Information
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <FormTextInput label="First Name" name="firstName" required={true} disabled={isOfficer} />
                        <FormTextInput label="Last Name" name="lastName" required={true} disabled={isOfficer} />
                    </Stack>
                    <FormPhoneInput
                        label="Mobile Phone Number"
                        fullPhoneFieldName="phoneNumberFull"
                        countryCodeFieldName="phoneCountryCode"
                        localNumberFieldName="phoneNumber"
                        required={true}
                        disabled={isOfficer}
                    />
                    {!isOfficer && <PhoneNumberAlert />}
                    <FormTextInput label="Email" name="email" required={true} disabled={isOfficer} />
                    <FormStringOptionSelect
                        label="Title"
                        name="title"
                        options={TITLE_OPTIONS}
                        placeholder="Select a title"
                        required={true}
                    />
                    <Stack spacing={1}>
                        <Box>
                            <FormTextInput label="SSN" name="ssn" required={!usePassport} disabled={usePassport} />
                        </Box>
                        <Box>
                            <FormCheckbox
                                label={`${isOfficer ? "I do" : "Does"} not have a SSN`}
                                name="usePassport"
                                sx={{ mt: 2.5 }}
                            />
                        </Box>
                    </Stack>
                    {usePassport && (
                        <Stack direction="row" spacing={2}>
                            <FormInputWrapper<IOfficerInformationFormData>
                                name="passportNumber"
                                label="Passport #"
                                required={true}
                            >
                                <ControllerTextField<IOfficerInformationFormData>
                                    name="passportNumber"
                                    controllerProps={{ rules: { required: true } }}
                                />
                            </FormInputWrapper>
                            <FormInputWrapper<IOfficerInformationFormData>
                                label="Passport Issuing Country"
                                name="nationality"
                                required={true}
                            >
                                <ControllerCountrySelect<IOfficerInformationFormData>
                                    name="nationality"
                                    controllerProps={{ rules: { required: true } }}
                                    useCountryOptions={usePassportCountryOptions}
                                    disableClearable={true}
                                />
                            </FormInputWrapper>
                        </Stack>
                    )}
                    <FormDatePicker name="dob" label="Date of Birth" required={true} disableFuture={true} />
                    <FormSpreadAddressInput<IOfficerInformationFormData>
                        label="Officer Personal Address"
                        description="Cannot be a PO box but international addresses are allowed"
                        addressFields={ADDRESS_FIELDS}
                    />
                    <Stack direction="row" justifyContent="space-between" pt={1}>
                        <div>{prevPageButton}</div>
                        <div>{nextPageButton}</div>
                    </Stack>
                </Stack>
            </form>
        </FormProvider>
    );
};
